import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  TextField,
  Button,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useAuthContext } from "../context/AuthContext";
import { UserService, User } from "../services/UserService";
import { jwtDecode } from "jwt-decode";

interface Permission {
  context_id: string;
  role: string;
}

interface DecodedToken {
  sub: string;
  email: string;
  contexts?: Permission[];
}

const Account: React.FC = () => {
  const { token } = useAuthContext();
  const [userData, setUserData] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchAccountData = async () => {
      if (!token) {
        setError("No authentication token found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Decode token to get permissions
        const decoded: DecodedToken = jwtDecode(token);
        setPermissions(decoded.contexts || []);

        // Fetch user profile data
        const userProfile = await UserService.getCurrentUserProfile();
        setUserData(userProfile);
      } catch (err: any) {
        console.error("Error fetching account data:", err);
        setError(err.message || "Failed to load account information");
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, [token]);

  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All password fields are required");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await UserService.updateCurrentUserProfile({ password: newPassword });
      setPasswordSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setPasswordError(err.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert severity="warning">No user data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Account Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Personal Information</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body1">{userData.username}</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">
                {userData.first_name} {userData.last_name}
              </Typography>
            </Box>

            {userData.date_of_birth && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Date of Birth
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(userData.date_of_birth)}
                  </Typography>
                </Box>
              </>
            )}

            {userData.created_at && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(userData.created_at)}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>

          {/* Email Information */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Email Addresses</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {userData.emails && userData.emails.length > 0 ? (
              userData.emails.map((emailItem, index) => (
                <Box key={index} mb={1}>
                  <Typography variant="body1">
                    {emailItem.email.email}
                  </Typography>
                  {index < userData.emails!.length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No email addresses found
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Permissions and Roles + Password Change */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <SecurityIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Permissions & Roles</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {permissions.length > 0 ? (
              <Box>
                {permissions.map((permission, index) => (
                  <Box key={index} mb={1}>
                    <Chip
                      label={`${permission.context_id}: ${permission.role}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                    {index < permissions.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No permissions found
              </Typography>
            )}
          </Paper>

          {/* Change Password */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <LockIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Change Password</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {passwordSuccess}
              </Alert>
            )}

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            <Box component="form" noValidate>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                size="small"
                helperText="Minimum 8 characters"
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                size="small"
              />
              <Button
                variant="contained"
                onClick={handlePasswordChange}
                disabled={changingPassword}
                sx={{ mt: 2 }}
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Account;
